package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initInstrumentationPgoOnKotlinNativePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/InstrumentationPgoOnKotlinNative.md", FrontMatterElement.Builder { addScalar("title", "Instrumentation PGO on Kotlin/Native"); addScalar("searchTitle", "Kotlin/Native PGO: pgo-instr-gen, llvm-profdata and the CG Profile wall"); addScalar("date", "2026-09-21"); addScalar("description", "A profile can be taken and applied on stock tools, with no fork. Turning it into a binary means going around the compiler's own LTO, which costs nothing measurable on a benchmark — and the profile returns 10–13 % there. What that leaves unanswered for a service, and the recipe."); addScalar("source", "https://github.com/youndie/pgo-native-spike"); }))
}

@Page("/blog/instrumentation-pgo-on-kotlin-native")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun InstrumentationPgoOnKotlinNativePage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A profile can be taken off a Kotlin/Native binary and applied back to its IR on stock tools, with no fork and no patched toolchain. Building an optimised binary out of the result means going around the compiler's own LTO — which on a dispatch benchmark costs nothing measurable, while the profile returns 10–13 %.")
        }
        org.jetbrains.compose.web.dom.Text(" What that detour does lose is internalisation: 3 335 defines against an ordinary build's 495, and a service binary 3.8× the size. Whether that costs a ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("service")
        }
        org.jetbrains.compose.web.dom.Text(" anything in CPU is not measured here, and it is the gap between this benchmark and a recommendation. The rest is how, why, and the numbers.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The recipe is below and it is seven commands; it also exists as ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/pgo-native-spike/blob/main/recipe/pgo.sh") {
            org.jetbrains.compose.web.dom.Text("a script that checks every step")
        }
        org.jetbrains.compose.web.dom.Text(", because a PGO pipeline that runs cleanly and applies nothing looks exactly like one that worked.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The wall is not the linker and it is not specific to large projects.")
        }
        org.jetbrains.compose.web.dom.Text(" A module carrying profile metadata cannot pass Kotlin/Native's own LTO or its codegen step, on a single file as much as on a service — the benchmark in this post is built around it too. The link itself replays byte for byte.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The subject is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/xyk") {
            org.jetbrains.compose.web.dom.Text("xyk")
        }
        org.jetbrains.compose.web.dom.Text(", a webhook gateway with SQLite on the request path — Kotlin/Native, Ktor, about 8 ms of CPU per request on four cores. The study, its raw logs, the recipe as a script and the backlog with the hypotheses that were dropped are in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/pgo-native-spike") {
            org.jetbrains.compose.web.dom.Text("pgo-native-spike")
        }
        org.jetbrains.compose.web.dom.Text("; the thresholds were written down before the first measurement and the amendments carry the date and the reason each was made.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Everything here is Kotlin 2.4.20 and LLVM 21 on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-need") }) {
        org.jetbrains.compose.web.dom.Text("What you need")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("llvm-profdata")
        }
        org.jetbrains.compose.web.dom.Text(" already ships with Kotlin/Native. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("opt")
            }
            org.jetbrains.compose.web.dom.Text(" does not")
        }
        org.jetbrains.compose.web.dom.Text(", and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("opt")
        }
        org.jetbrains.compose.web.dom.Text(" is what runs both PGO passes. A normal install pulls the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("essentials")
        }
        org.jetbrains.compose.web.dom.Text(" LLVM bundle; the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("dev")
        }
        org.jetbrains.compose.web.dom.Text(" bundle is a separate download named in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("konan.properties")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""grep -E 'llvm\.linux_x64\.dev|dependenciesUrl' \
  ~/.konan/kotlin-native-prebuilt-linux-x86_64-2.4.20/konan/konan.properties
# llvm.linux_x64.dev=llvm-21-x86_64-linux-dev-116
# dependenciesUrl = https://download.jetbrains.com/kotlin/native

curl -fsSL -O https://download.jetbrains.com/kotlin/native/resources/llvm/21-x86_64-linux/llvm-21-x86_64-linux-dev-116.tar.gz
tar xzf llvm-21-x86_64-linux-dev-116.tar.gz -C ~/.konan/dependencies/
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It carries ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("opt")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("clang")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("llvm-ar")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libclang_rt.profile.a")
        }
        org.jetbrains.compose.web.dom.Text(", and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("opt --print-passes")
        }
        org.jetbrains.compose.web.dom.Text(" lists ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("pgo-instr-gen")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("pgo-instr-use")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("instrprof")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("pgo-icall-prom")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-recipe") }) {
        org.jetbrains.compose.web.dom.Text("The recipe")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""KN=~/.konan/kotlin-native-prebuilt-linux-x86_64-2.4.20
L=~/.konan/dependencies/llvm-21-x86_64-linux-dev-116

# 1. Compile normally and keep the IR at the point where Kotlin code and the runtime are one
#    module. The directory must already exist: kotlinc warns and exits 0 if it does not.
mkdir -p ir
${'$'}KN/bin/kotlinc-native -opt -Xsave-llvm-ir-directory=${'$'}PWD/ir \
    -Xsave-llvm-ir-after=LinkBitcodeDependencies -o base app.kt

# 2. Instrument.
${'$'}L/bin/opt -passes="pgo-instr-gen,instrprof" ir/out.LinkBitcodeDependencies.ll -o instr.bc

# 3. Rebuild the profile runtime with an IR-level version variable (see below).
printf 'long long __llvm_profile_raw_version = (1LL << 56) | 10;\n' > version.c
${'$'}L/bin/clang -c -o version.o version.c
mkdir rtx && cd rtx && ${'$'}L/bin/llvm-ar x ${'$'}L/lib/clang/21/lib/x86_64-unknown-linux-gnu/libclang_rt.profile.a
rm -f InstrProfilingVersionVar.c.o && cp ../version.o . \
  && ${'$'}L/bin/llvm-ar rcs ../libprofile-ir.a *.o && cd ..

# 4. Link the instrumented binary. -u__llvm_profile_runtime is load-bearing (see below).
${'$'}KN/bin/kotlinc-native -opt -Xcompile-from-bitcode=${'$'}PWD/instr.bc \
    -linker-option -u__llvm_profile_runtime -linker-option ${'$'}PWD/libprofile-ir.a -o train

# 5. Train, and merge.
LLVM_PROFILE_FILE=${'$'}PWD/p-%p.profraw ./train.kexe
${'$'}L/bin/llvm-profdata merge -output=p.profdata p-*.profraw
${'$'}L/bin/llvm-profdata show p.profdata | grep -i 'instrumentation level'   # must say IR

# 6. Apply.
${'$'}L/bin/opt -passes="pgo-instr-use" -pgo-test-profile-file=${'$'}PWD/p.profdata \
    ir/out.LinkBitcodeDependencies.ll -o applied.bc
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Step 6 leaves annotated IR, not a binary. Getting a binary out of it needs a seventh command, and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("CG Profile")
            }
            org.jetbrains.compose.web.dom.Text(" collision described below fires here too")
        }
        org.jetbrains.compose.web.dom.Text(" — so the LTO pipelines have to be neutered and the optimisation done in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("opt")
        }
        org.jetbrains.compose.web.dom.Text(" instead:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""# 7. Optimise externally, because kotlinc's own pipelines cannot run over a module that carries
#    profile metadata, and hand it back for codegen and linking.
${'$'}L/bin/opt -passes="default<O3>" applied.bc -o optimised.bc
${'$'}KN/bin/kotlinc-native -opt -Xcompile-from-bitcode=${'$'}PWD/optimised.bc     -Xllvm-module-passes=verify -Xllvm-lto-passes=verify -o a2
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Arms still have to be compared along one route")
        }
        org.jetbrains.compose.web.dom.Text(", which is what the numbers below do — but the route itself turns out to be nearly free on this benchmark: 1.075 ns/op against the ordinary build's 1.063 on the interface call. An earlier version of this post put that cost at 57–68 %, which was a mistake: those two figures came from different machines, and the study they came from did not record the host.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Safepoints are the countable difference between the routes, and they are not the cost.")
        }
        org.jetbrains.compose.web.dom.Text(" Kotlin/Native's own pipeline strips the polls; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("default<O3>")
        }
        org.jetbrains.compose.web.dom.Text(" in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("opt")
        }
        org.jetbrains.compose.web.dom.Text(" does not, because the passes that do it are not in it:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("same source, final IR")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("defines")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("call … safePoint")
                    }
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("ordinary build")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("495")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("25")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("step-7 route")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("3 335")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("4 189")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That looks like an explanation and it is not one. Removing all 4 179 remaining poll sites from the module and rebuilding changed the no-dispatch control by nothing measurable — 0.470 ns/op against 0.447. Most of those polls are in stdlib the benchmark never executes, and the hot loop does not pay for them. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The difference is real, countable and not the mechanism")
        }
        org.jetbrains.compose.web.dom.Text(", which is the sort of thing a toggle establishes and a symbol count does not.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-two-steps-that-are-load-bearing") }) {
        org.jetbrains.compose.web.dom.Text("The two steps that are load-bearing")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both were checked by removing them, because a PGO pipeline that runs cleanly and applies nothing looks exactly like one that worked.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-u__llvm_profile_runtime")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Text(" Without it the binary links, runs, exits successfully and writes ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("no ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text(".profraw")
            }
            org.jetbrains.compose.web.dom.Text(" at all")
        }
        org.jetbrains.compose.web.dom.Text(". Nothing warns. The flag forces in the registration object that the profile runtime's constructors hang off.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The replaced version variable.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libclang_rt.profile.a")
        }
        org.jetbrains.compose.web.dom.Text(" ships ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("InstrProfilingVersionVar.c.o")
        }
        org.jetbrains.compose.web.dom.Text(" declaring the raw profile version as front-end. Leave it and the profile merges as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Instrumentation level: Front-end")
        }
        org.jetbrains.compose.web.dom.Text(", and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("pgo-instr-use")
        }
        org.jetbrains.compose.web.dom.Text(" then refuses it outright:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""error: p.profdata: Not an IR level instrumentation profile
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Replacing that one object with a one-line C file is the whole fix.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-it-stops-on-a-real-service") }) {
        org.jetbrains.compose.web.dom.Text("Where it stops on a real service")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The recipe above ends at a binary. Where a service and a single-file program part company is step 7 — the workaround that makes that binary possible is the thing a service cannot afford.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-Xcompile-from-bitcode")
            }
            org.jetbrains.compose.web.dom.Text(" has no klib graph.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DependenciesTracker")
        }
        org.jetbrains.compose.web.dom.Text(" builds the native dependency list from the klib graph during a normal compilation, and a resume from bitcode has none — so every native symbol a klib would have contributed goes undefined. Supplying archives by hand is unbounded: adding librdkafka's five moved the failure on to sqlx4k's symbols.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The fix is not to resume from bitcode at all. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xverbose-phases=Linker")
        }
        org.jetbrains.compose.web.dom.Text(" prints the whole ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ld.lld")
        }
        org.jetbrains.compose.web.dom.Text(" invocation and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xtemporary-files-dir")
        }
        org.jetbrains.compose.web.dom.Text(" keeps the object it refers to, so the link can be replayed with a different object substituted:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""${'$'}KN/bin/kotlinc-native -opt -Xtemporary-files-dir=${'$'}PWD/tmp -Xverbose-phases=Linker \
    -o base app.kt 2>&1 | grep -oE '/[^ ]*ld\.lld .*' > link.cmd
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Replaying that line with the compiler's own object reproduces the ordinary build ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("byte for byte")
        }
        org.jetbrains.compose.web.dom.Text(" — on a 20 MB statically linked service binary, not only on a toy. With the instrumented object substituted it produces a working training binary, because instrumented bitcode carries no profile metadata and kotlinc runs its own full pipeline over it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The use side is where it stops.")
        }
        org.jetbrains.compose.web.dom.Text(" A module that ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("does")
        }
        org.jetbrains.compose.web.dom.Text(" carry profile metadata makes kotlinc emit the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CG Profile")
        }
        org.jetbrains.compose.web.dom.Text(" module flag twice — once from its LTO pipeline, once from the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("clang++")
        }
        org.jetbrains.compose.web.dom.Text(" codegen step — and reject its own module:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""module flag identifiers must be unique (or of 'require' type)
!"CG Profile"
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Neither copy comes from the input: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("opt -passes=pgo-instr-use")
        }
        org.jetbrains.compose.web.dom.Text(" emits no such flag. The only ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xllvm-lto-passes")
        }
        org.jetbrains.compose.web.dom.Text(" value that avoids the collision is one that does no LTO — which is exactly the workaround step 7 uses on the benchmark.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("So the service arm can be built; it just cannot be built in a form worth measuring against the shipped binary.")
        }
        org.jetbrains.compose.web.dom.Text(" Kotlin/Native's own LTO internalises and dead-strips 3 027 defines down to 411, and the externally optimised binary came out 3.8× larger. On the benchmark that trade is acceptable because both arms take the identical route and the question is whether the mechanism fires. On the service the question is what PGO adds to ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("the binary that ships")
        }
        org.jetbrains.compose.web.dom.Text(", and a 3.8× binary is not that binary.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The same sentence covers both, and an earlier version of this post did not say it.")
        }
        org.jetbrains.compose.web.dom.Text(" Comparing two arms along one route isolates the mechanism and says nothing about what PGO would add to the production pipeline. That is true of the benchmark numbers above as much as of a service arm — the difference is only that on the benchmark isolating the mechanism was the whole question.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-profile-is-worth") }) {
        org.jetbrains.compose.web.dom.Text("What the profile is worth")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two independent measurements, because \"PGO helps\" is not a number.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Indirect call promotion, on a microbenchmark.")
        }
        org.jetbrains.compose.web.dom.Text(" Eight implementors of an interface so the compiler's closed-world devirtualisation cannot resolve the site by itself, receivers drawn from a controlled distribution, nine interleaved rounds, 99 % intervals:")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Three builds, nine interleaved rounds, 99 % intervals, one machine and one session — which also answers the question a two-arm table cannot: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("is the PGO binary faster than one built the ordinary way, with no detour at all?")
        }
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("build")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("interface call")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("virtual call")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("no dispatch")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("ordinary, no PGO")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("1.066 ± 0.025")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("0.804 ± 0.031")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("0.486 ± 0.029")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("step-7 route, no profile")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("1.071 ± 0.016")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("0.806 ± 0.028")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("0.494 ± 0.050")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("step-7 route, with profile")
                    }
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0.959 ± 0.026")
                    }
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0.699 ± 0.036")
                    }
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("0.498 ± 0.056")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The detour is free within resolution")
        }
        org.jetbrains.compose.web.dom.Text(" — every one of its three intervals overlaps the ordinary build's. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("And the profile is worth −10.0 % on the interface call and −13.1 % on the virtual call against an ordinary build")
        }
        org.jetbrains.compose.web.dom.Text(", both separating, while the no-dispatch control separates from nothing at all. That last clause is what makes the other two readable.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So on this benchmark PGO is a win, and the detour is not a tax you pay for it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An earlier version of this post reported the opposite — that an ordinary build was 34 % faster — from three non-interleaved runs whose numbers came off two different machines. The correction is why the table above says how it was measured.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The control of known outcome sits in an earlier run and is quoted as a ratio only.")
        }
        org.jetbrains.compose.web.dom.Text(" With each arm trained on its own distribution, eight receivers in rotation gained nothing — +5.2 % and +3.5 %, intervals overlapping — which is what the measured ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("icp-remaining-percent-threshold = 30")
        }
        org.jetbrains.compose.web.dom.Text(" predicts at 12.5 % per receiver. Those runs are from a machine the log does not name, so only the ratio is used; the levels above supersede theirs.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The IR carries the expected shape at those sites — a guard against the dominant target with the callee's body inlined behind it — six such sites against zero without the profile. The uniform row is the control: at 12.5 % per receiver nothing clears the measured ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("icp-remaining-percent-threshold = 30")
        }
        org.jetbrains.compose.web.dom.Text(", and nothing is promoted.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("That is 0.11 ns per promoted call")
        }
        org.jetbrains.compose.web.dom.Text(" — 0.113 on the interface call and 0.108 on the virtual one — and it is the bound to carry into a service ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("for promotion alone")
        }
        org.jetbrains.compose.web.dom.Text(", with a callee whose body is a single addition. It says nothing about code layout, hot/cold splitting or hotness-scaled inlining thresholds, which are separate PGO effects:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("to move")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("needs")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 % of a 1 ms request")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("~90 000 promoted calls")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 % of a 7.8 ms request")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("~710 000 promoted calls")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("(An earlier version of this post said 0.16–0.18 ns and put those counts at 60 000 and 460 000. Both were derived from the superseded levels.)")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("And the share of CPU PGO can touch, on the service.")
        }
        org.jetbrains.compose.web.dom.Text(" Sampled with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("perf")
        }
        org.jetbrains.compose.web.dom.Text(" at 50–70 % of each endpoint's own saturation, attributed by symbol with ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/pgo-native-spike/blob/main/scripts/attribution.py") {
            org.jetbrains.compose.web.dom.Text("a grammar that has its own control")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("endpoint")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("Kotlin self")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("Kotlin + runtime")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("GET /health/live")
                    }
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("32.55 %")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("50.38 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("POST /hooks/{id}")
                    }
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("21.22 %")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("32.33 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("GET /api/events")
                    }
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("16.40 %")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("37.13 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("GET /journal")
                    }
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("13.84 %")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("41.24 %")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The gradient runs the wrong way for this: the endpoint that parses nothing and touches no database has twice the Kotlin share of the one that renders a page. At a 20–40 % bucket, a 5–15 % return on the code PGO touches is 1–6 % of a request, and the difference two interleaved arms can resolve on this stand is 5 %.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("That 5–15 % is a prior, not a measurement")
        }
        org.jetbrains.compose.web.dom.Text(" — it is the range this study wrote down for C and C++ servers before starting, and it is the one number here that was not measured. Nothing in the arithmetic above improves if the real figure is higher, because the bucket is what bounds it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For scale, on the same service and the same stand, one build property — Kotlin/Native's paged allocator, which is the platform default — is worth ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("19.01 % ± 2.33 %")
        }
        org.jetbrains.compose.web.dom.Text(" of CPU per request.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("That is not 19 % lying on the floor, and the pinned build turns the allocator off on purpose.")
        }
        org.jetbrains.compose.web.dom.Text(" The paged allocator keeps a page per size class ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("per thread")
        }
        org.jetbrains.compose.web.dom.Text(" for the life of the thread, so resident memory follows thread count rather than live heap: under a 64 MiB limit this service survived 1 run in 10 with it and 10 in 10 without, which is what ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/under-a-container-limit") {
            org.jetbrains.compose.web.dom.Text("Ktor on Kotlin/Native under a container limit")
        }
        org.jetbrains.compose.web.dom.Text(" is about. The honest form is that the memory criterion costs this service a fifth of its request CPU. What nobody has measured is whether the paged allocator fits that limit with fewer threads — if it does, the trade dissolves.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Either way, machine-code quality is not where this service spends its time.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-long-a-profile-lives") }) {
        org.jetbrains.compose.web.dom.Text("How long a profile lives")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A profile is keyed by function name and CFG hash, so the question is what a commit costs. Trained on one revision, applied to earlier ones, counting both functions and the counters they carry:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("distance from the trained revision")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("retained, functions")
                }
                org.jetbrains.compose.web.dom.Th(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("retained, counter weight")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("build-only commit, no source change")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("99.965 %")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("100.000 %")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("two commits")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("99.896 %")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("99.999 %")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("three commits")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("99.584 %")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("99.968 %")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("four commits, across a framework minor version")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.Text("96.460 %")
                }
                org.jetbrains.compose.web.dom.Td(attrs = { style {property("text-align", "right")} }) {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("99.683 %")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Counting functions overstates the loss, because what stops matching is cold. That last row is a single version transition and is not a rate for framework bumps in general.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("What moves is not names.")
        }
        org.jetbrains.compose.web.dom.Text(" Of the functions that disappear by name, most are generated lambdas inside the module whose code actually changed, and together they carry 19 counters out of 1.46 billion. The expensive losses are CFG hash mismatches in ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("shared")
        }
        org.jetbrains.compose.web.dom.Text(" code: one stdlib function, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlinx.cinterop.DeferScope#executeAllDeferred")
        }
        org.jetbrains.compose.web.dom.Text(", accounted for 442 333 of 468 374 lost counters with its own source unchanged.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The reason is worth knowing on its own. Kotlin/Native devirtualises on a closed world, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("invoke")
        }
        org.jetbrains.compose.web.dom.Text(" on the deferred lambdas becomes a chain of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kclass")
        }
        org.jetbrains.compose.web.dom.Text(" guards whose arm count tracks the set of implementations reachable ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("in the whole program")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-llvm") }) { org.jetbrains.compose.web.dom.Text("""; two reachable defer-lambdas — a guard, then two direct calls
%8 = icmp eq ptr %7, @"kclass:…getAddressInfo${'$'}${'$'}inlined${'$'}memScoped${'$'}1#internal"
br i1 %8, label %when_case1, label %when_next2
  call void @"kfun:…getAddressInfo${'$'}${'$'}inlined${'$'}memScoped${'$'}1.invoke#internal"
  call void @"kfun:kotlinx.cinterop.refTo${'$'}${'$'}inlined${'$'}usingPinned${'$'}1…invoke#internal"

; one reachable defer-lambda — one direct call, no comparison at all
  call void @"kfun:…getAddressInfo${'$'}${'$'}inlined${'$'}memScoped${'$'}1.invoke#internal"
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Isolated on two builds of one revision differing only by an added ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("defer { }")
        }
        org.jetbrains.compose.web.dom.Text(" in application code: the binary grows 984 bytes and two untouched stdlib symbols move — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlinx.cinterop.ArenaBase#clearImpl")
        }
        org.jetbrains.compose.web.dom.Text(" from 283 to 331 bytes, and the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("usingPinned")
        }
        org.jetbrains.compose.web.dom.Text(" getter lambda from 223 to 297.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The difference from C and C++ is pass order, not the absence of the transformation.")
        }
        org.jetbrains.compose.web.dom.Text(" C++ has the same class of whole-program rewriting through LTO and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-fwhole-program-vtables")
        }
        org.jetbrains.compose.web.dom.Text(". What differs is where the profile takes its key: LLVM hashes early, before the inliner and before whole-program devirtualisation, so a function's own source fixes its hash. Kotlin/Native devirtualises in the frontend, before LLVM IR exists at all, so ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("every instrumentation point reachable through ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("opt")
            }
            org.jetbrains.compose.web.dom.Text(" is downstream of it")
        }
        org.jetbrains.compose.web.dom.Text(". A profile hash here is a property of the whole program's composition rather than of the function's source, and no choice of instrumentation point changes that.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-stand") }) {
        org.jetbrains.compose.web.dom.Text("The stand")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two Hetzner machines, 4 vCPU each, Ubuntu with glibc 2.43: the subject on one, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("k6")
        }
        org.jetbrains.compose.web.dom.Text(" on the other, never co-located. The unit is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("microseconds of CPU per request")
        }
        org.jetbrains.compose.web.dom.Text(", read from the subject's own process accounting over a window in which the offered rate is shown to be met and nothing is dropped.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Arms are interleaved round by round and the first round is discarded. Repeating the whole protocol with the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("same binary")
        }
        org.jetbrains.compose.web.dom.Text(" on both arms puts the paired standard deviation at ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("2.92 % per round")
        }
        org.jetbrains.compose.web.dom.Text(", which is what any two-arm difference is measured against: at eight counted rounds the resolvable difference is 5 %.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two cautions from running it. That 2.92 % was characterised on a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("statically linked")
        }
        org.jetbrains.compose.web.dom.Text(" binary; a dynamically linked one ran at roughly twice the spread, and disabling ASLR did not clearly account for the gap. And the microbenchmark numbers above are nanoseconds per operation in a loop — they are about dispatch, not about a service, which is exactly why the per-call bound is quoted next to them.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("No service arm was measured, and the reason is narrower than \"it cannot be built\".")
        }
        org.jetbrains.compose.web.dom.Text(" It can be built, by the same step-7 workaround the benchmark uses. What cannot be had is an arm comparable to the shipped binary, because that workaround discards Kotlin/Native's own LTO. The ceiling and the per-call bound predict an effect below this stand's resolution anyway; that is a prediction and not a measurement, and it is why the weakened arm was not run.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("What promotion is worth under Kotlin/Native's own pipeline is unknown.")
        }
        org.jetbrains.compose.web.dom.Text(" Every figure here is from the external-LTO route. Without internalisation LLVM's inliner sees a different program, and the native LTO may already capture part of what promotion captures here — so 11-13 % is the mechanism's effect ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("on that route")
        }
        org.jetbrains.compose.web.dom.Text(", not an increment to a production build. The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CG Profile")
        }
        org.jetbrains.compose.web.dom.Text(" wall is precisely what stops anyone measuring the increment.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A second way round the wall, also untried: rebuild the internalisation list.")
        }
        org.jetbrains.compose.web.dom.Text(" What the detour loses is not speed on a benchmark — it is internalisation, and with it the 3 335 defines against an ordinary build's 495, and a service binary 3.8× the size. That list is recoverable: dump the IR of an ordinary build after its own LTO, take the defines with external linkage, and hand them to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("opt -passes='internalize,globaldce,default<O3>' -internalize-public-api-list=…")
        }
        org.jetbrains.compose.web.dom.Text(". If the resulting binary matches an ordinary one in size, the detour stops being a detour and a service arm becomes measurable. One thing to check first: Kotlin/Native may run ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("lto<O3>")
        }
        org.jetbrains.compose.web.dom.Text(" rather than ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("default<O3>")
        }
        org.jetbrains.compose.web.dom.Text(", which its own pipeline string would show.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("linuxX64")
            }
            org.jetbrains.compose.web.dom.Text(" only.")
        }
        org.jetbrains.compose.web.dom.Text(" Nothing here was run on macOS or on an Apple target, and the profile runtime is rebuilt from a path with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("x86_64-unknown-linux-gnu")
        }
        org.jetbrains.compose.web.dom.Text(" in it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("CG Profile")
            }
            org.jetbrains.compose.web.dom.Text(" collision looks avoidable, and I have not tried it.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("clang")
        }
        org.jetbrains.compose.web.dom.Text(" enables the pass only with the integrated assembler — ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/llvm/llvm-project/blob/242996efee4aaa30dd0ddb4359675b8bc103294a/clang/lib/CodeGen/BackendUtil.cpp#L908") {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("BackendUtil.cpp")
            }
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PTO.CallGraphProfile = !CodeGenOpts.DisableIntegratedAS")
        }
        org.jetbrains.compose.web.dom.Text(", with a comment saying non-integrated assemblers do not recognise the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".cgprofile")
        }
        org.jetbrains.compose.web.dom.Text(" section. So ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-fno-integrated-as")
        }
        org.jetbrains.compose.web.dom.Text(" on the codegen step should remove the second copy of the flag. The price is a system ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("as")
        }
        org.jetbrains.compose.web.dom.Text(" from binutils and building both arms the same way, and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("whether it works through kotlinc on Kotlin/Native is untested")
        }
        org.jetbrains.compose.web.dom.Text(" — what is established is only that the mechanism in clang is this one. The blunter lever is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xdisable-phases")
        }
        org.jetbrains.compose.web.dom.Text(", which exists, but ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xlist-phases")
        }
        org.jetbrains.compose.web.dom.Text(" printed nothing on 2.4.20, so the phase name and its cost are both unknown.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The static link and the profile runtime are incompatible as shipped.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-static")
        }
        org.jetbrains.compose.web.dom.Text(" plus the profile runtime fails with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("undefined hidden symbol: _DYNAMIC")
        }
        org.jetbrains.compose.web.dom.Text(". Only the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("training")
        }
        org.jetbrains.compose.web.dom.Text(" binary needs the runtime, and a profile is keyed by name and CFG hash rather than by linkage, so training dynamically and applying to the pinned module is sound — but it has to be done deliberately.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("One subject, one workload.")
        }
        org.jetbrains.compose.web.dom.Text(" Every share above comes from one service with SQLite on the request path. The recipe transfers; the shares do not.")
    }
}
